import { verifyMemberCode, type VerifyOptions } from './member-code';
import type { KeySet } from './keys';

export interface CheckinEvent {
  eventId: string; // client-generated unique id
  kioskId: string;
  branchId: string;
  code: string; // the member-shown proof
  scannedAtMs: number; // kiosk local clock, epoch ms
  decision: 'allow' | 'deny';
}

export interface SyncBatch {
  batchId: string;
  kioskId: string;
  events: CheckinEvent[];
}

/** Pure in-memory queue. Persistence is the caller's job (save `snapshot()`, load with `restore`). */
export class OfflineCheckinQueue {
  private events = new Map<string, CheckinEvent>();
  private proofs = new Set<string>();
  private sent = new Set<string>();

  constructor(private kioskId: string) {}

  /** Rebuild after a restart. Events that were in flight become pending; the server dedupes retries. */
  static restore(kioskId: string, events: CheckinEvent[]): OfflineCheckinQueue {
    const q = new OfflineCheckinQueue(kioskId);
    for (const e of events) q.append(e);
    return q;
  }

  /** Returns false if the event belongs to another kiosk or (by id or identical proof) was already queued. */
  append(e: CheckinEvent): boolean {
    if (e.kioskId !== this.kioskId) return false;
    const proofKey = `${e.kioskId}|${e.code}`;
    if (this.events.has(e.eventId) || this.proofs.has(proofKey)) return false;
    this.events.set(e.eventId, e);
    this.proofs.add(proofKey);
    return true;
  }

  pending(): CheckinEvent[] {
    return this.snapshot().filter((e) => !this.sent.has(e.eventId));
  }

  /** Every event not yet acknowledged, including batches in flight. This is what must be persisted. */
  snapshot(): CheckinEvent[] {
    return [...this.events.values()].sort((a, b) => a.scannedAtMs - b.scannedAtMs);
  }

  toBatch(batchId: string, max = 500): SyncBatch {
    const events = this.pending().slice(0, max);
    for (const e of events) this.sent.add(e.eventId);
    return { batchId, kioskId: this.kioskId, events };
  }

  /** Call when the server did NOT acknowledge a batch, so its events are retried. */
  requeue(batch: SyncBatch): void {
    for (const e of batch.events) this.sent.delete(e.eventId);
  }

  /** Call on acknowledgement; frees memory. */
  acknowledge(batch: SyncBatch): void {
    for (const e of batch.events) {
      const held = this.events.get(e.eventId);
      if (held) this.proofs.delete(`${held.kioskId}|${held.code}`);
      this.events.delete(e.eventId);
      this.sent.delete(e.eventId);
    }
  }
}

export type ReconcileFlag = 'bad_proof' | 'duplicate' | 'late' | 'too_old' | 'future_timestamp' | 'denied_at_kiosk' | 'kiosk_mismatch';
export interface ReconcileOutcome {
  eventId: string;
  status: 'accepted' | 'rejected';
  memberId?: string;
  flags: ReconcileFlag[];
}
export interface ReconcileOptions extends VerifyOptions {
  nowMs: number;
  /** Events older than this are flagged `late` (still accepted if proof valid). Default 24h. */
  maxLateMs?: number;
  /**
   * Events older than this are rejected, not just flagged. Unset = accept any age. `scannedAtMs` is a
   * claim, so without this limit a stolen key can back-date forged events into its old validity range.
   */
  maxAcceptAgeMs?: number;
  /** The authenticated kiosk's branch. Set it: a member code does not sign the branch. */
  expectedBranchId?: string;
  /** Proofs (`kioskId|code`) already recorded by earlier batches. */
  knownProofs?: Set<string>;
  /** eventIds already recorded by earlier batches. */
  knownEventIds?: Set<string>;
}

/**
 * Server: re-verify each proof at the time it was scanned (so the kiosk's clock claim
 * is bound by the key's validity), flag duplicates and late arrivals.
 * `batch.kioskId` must come from the authenticated kiosk session, never from the request body:
 * every event has to match it, which is what makes `kioskId|code` a sound replay key.
 */
export async function reconcile(batch: SyncBatch, keys: KeySet, opts: ReconcileOptions): Promise<ReconcileOutcome[]> {
  const maxLate = opts.maxLateMs ?? 24 * 3_600_000;
  const proofs = new Set(opts.knownProofs ?? []);
  const ids = new Set(opts.knownEventIds ?? []);
  const out: ReconcileOutcome[] = [];
  for (const e of batch.events) {
    const flags: ReconcileFlag[] = [];
    if (e.kioskId !== batch.kioskId || (opts.expectedBranchId !== undefined && e.branchId !== opts.expectedBranchId)) {
      out.push({ eventId: e.eventId, status: 'rejected', flags: ['kiosk_mismatch'] });
      continue;
    }
    const proofKey = `${e.kioskId}|${e.code}`;
    if (ids.has(e.eventId) || proofs.has(proofKey)) {
      out.push({ eventId: e.eventId, status: 'rejected', flags: ['duplicate'] });
      continue;
    }
    const v = await verifyMemberCode(e.code, keys, e.scannedAtMs, opts);
    if (e.scannedAtMs > opts.nowMs + 5 * 60_000) flags.push('future_timestamp');
    if (opts.maxAcceptAgeMs !== undefined && opts.nowMs - e.scannedAtMs > opts.maxAcceptAgeMs) flags.push('too_old');
    if (!v.ok || flags.length > 0) {
      if (!v.ok) flags.push('bad_proof');
      out.push({ eventId: e.eventId, status: 'rejected', flags });
      continue;
    }
    ids.add(e.eventId);
    proofs.add(proofKey);
    if (opts.nowMs - e.scannedAtMs > maxLate) flags.push('late');
    if (e.decision === 'deny') flags.push('denied_at_kiosk');
    out.push({ eventId: e.eventId, status: 'accepted', memberId: v.memberId, flags });
  }
  return out;
}
